import React, { useState, useEffect, useCallback } from 'react';
import { backupsApi } from '../services/api';
import './BackupRestore.css';

function BackupRestore() {
  // Table groups state
  const [tableGroups, setTableGroups] = useState([]);
  const [mediaInfo, setMediaInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Create backup state
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [includeMedia, setIncludeMedia] = useState(false);
  const [destination, setDestination] = useState('server');
  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState(null);

  // Server backups state
  const [serverBackups, setServerBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);

  // Restore state
  const [restoreSource, setRestoreSource] = useState('server'); // 'server' or 'upload'
  const [restoreFilename, setRestoreFilename] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [inspectedManifest, setInspectedManifest] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [restoreGroups, setRestoreGroups] = useState([]);
  const [restoreMedia, setRestoreMedia] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const loadTableGroups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await backupsApi.getTables();
      setTableGroups(response.data.groups);
      setMediaInfo(response.data.media);
    } catch (error) {
      console.error('Failed to load table groups:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadServerBackups = useCallback(async () => {
    try {
      setLoadingBackups(true);
      const response = await backupsApi.list();
      setServerBackups(response.data.backups);
    } catch (error) {
      console.error('Failed to load backups:', error);
    } finally {
      setLoadingBackups(false);
    }
  }, []);

  useEffect(() => {
    loadTableGroups();
    loadServerBackups();
  }, [loadTableGroups, loadServerBackups]);

  // Toggle group selection for backup creation
  const toggleGroup = (groupId) => {
    setSelectedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Select/deselect all groups
  const toggleAllGroups = () => {
    if (selectedGroups.length === tableGroups.length) {
      setSelectedGroups([]);
    } else {
      setSelectedGroups(tableGroups.map(g => g.id));
    }
  };

  // Create backup
  const handleCreateBackup = async () => {
    if (selectedGroups.length === 0) {
      setCreateMessage({ type: 'error', text: 'Bitte wähle mindestens eine Tabellengruppe aus.' });
      return;
    }

    setCreating(true);
    setCreateMessage(null);

    try {
      const response = await backupsApi.create({
        table_groups: selectedGroups,
        include_media: includeMedia,
        destination: destination,
      });

      if (destination === 'download') {
        // Trigger file download
        const blob = new Blob([response.data], { type: 'application/gzip' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const contentDisposition = response.headers['content-disposition'];
        const filename = contentDisposition
          ? contentDisposition.split('filename="')[1]?.replace('"', '')
          : 'backup.tar.gz';
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setCreateMessage({ type: 'success', text: 'Backup heruntergeladen.' });
      } else {
        setCreateMessage({ type: 'success', text: response.data.message });
        loadServerBackups();
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Fehler beim Erstellen des Backups.';
      setCreateMessage({ type: 'error', text: msg });
    } finally {
      setCreating(false);
    }
  };

  // Download server backup
  const handleDownloadBackup = async (filename) => {
    try {
      const response = await backupsApi.download(filename);
      const blob = new Blob([response.data], { type: 'application/gzip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  // Delete server backup
  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(`Backup "${filename}" wirklich löschen?`)) return;
    try {
      await backupsApi.delete(filename);
      loadServerBackups();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  // Inspect backup
  const handleInspect = async () => {
    setInspecting(true);
    setInspectedManifest(null);
    setRestoreGroups([]);

    try {
      let response;
      if (restoreSource === 'server') {
        if (!restoreFilename) return;
        response = await backupsApi.inspect(restoreFilename);
      } else {
        if (!uploadFile) return;
        const reader = new FileReader();
        const bytes = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(new Uint8Array(reader.result));
          reader.onerror = reject;
          reader.readAsArrayBuffer(uploadFile);
        });
        response = await backupsApi.inspectUpload(bytes);
      }

      setInspectedManifest(response.data.manifest);
      // Pre-select all groups from manifest
      setRestoreGroups(response.data.manifest.table_groups || []);
      setRestoreMedia(response.data.manifest.include_media || false);
    } catch (error) {
      console.error('Inspect failed:', error);
    } finally {
      setInspecting(false);
    }
  };

  // Toggle restore group selection
  const toggleRestoreGroup = (groupId) => {
    setRestoreGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Restore backup
  const handleRestore = async () => {
    setRestoring(true);
    setRestoreResult(null);

    try {
      let response;
      if (restoreSource === 'server') {
        response = await backupsApi.restore({
          filename: restoreFilename,
          selected_groups: restoreGroups,
          include_media: restoreMedia,
          confirm_overwrite: true,
        });
      } else {
        const reader = new FileReader();
        const bytes = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(new Uint8Array(reader.result));
          reader.onerror = reject;
          reader.readAsArrayBuffer(uploadFile);
        });
        response = await backupsApi.restoreUpload(bytes);
      }

      setRestoreResult({ type: 'success', data: response.data });
    } catch (error) {
      const msg = error.response?.data?.message || 'Fehler bei der Wiederherstellung.';
      setRestoreResult({ type: 'error', text: msg });
    } finally {
      setRestoring(false);
      setShowConfirmDialog(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('de-DE');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="backup-restore">
        <div className="backup-header">
          <h1>Sichern & Wiederherstellen</h1>
        </div>
        <div className="backup-loading">Lade...</div>
      </div>
    );
  }

  return (
    <div className="backup-restore">
      <div className="backup-header">
        <h1>Sichern & Wiederherstellen</h1>
        <p className="backup-subtitle">Datenbank-Backups erstellen, verwalten und wiederherstellen</p>
      </div>

      {/* Section 1: Create Backup */}
      <section className="backup-section" aria-labelledby="create-backup-heading">
        <h2 id="create-backup-heading">Backup erstellen</h2>

        <div className="backup-groups">
          <div className="backup-groups-header">
            <h3>Tabellengruppen</h3>
            <button
              className="btn-link"
              onClick={toggleAllGroups}
              aria-label={selectedGroups.length === tableGroups.length ? 'Alle abwählen' : 'Alle auswählen'}
            >
              {selectedGroups.length === tableGroups.length ? 'Alle abwählen' : 'Alle auswählen'}
            </button>
          </div>

          <div className="backup-groups-grid">
            {tableGroups.map(group => (
              <label key={group.id} className="backup-group-card">
                <input
                  type="checkbox"
                  checked={selectedGroups.includes(group.id)}
                  onChange={() => toggleGroup(group.id)}
                  aria-label={`${group.label} (${group.total_rows} Zeilen)`}
                />
                <div className="backup-group-info">
                  <span className="backup-group-label">{group.label}</span>
                  <span className="backup-group-count">{group.total_rows.toLocaleString('de-DE')} Zeilen</span>
                  <span className="backup-group-tables">
                    {group.tables.map(t => t.name).join(', ')}
                  </span>
                </div>
              </label>
            ))}
          </div>

          {mediaInfo && (
            <label className="backup-media-option">
              <input
                type="checkbox"
                checked={includeMedia}
                onChange={(e) => setIncludeMedia(e.target.checked)}
              />
              <div className="backup-group-info">
                <span className="backup-group-label">Medien (Uploads)</span>
                <span className="backup-group-count">
                  {mediaInfo.files_count} Dateien ({formatBytes(mediaInfo.total_size)})
                </span>
              </div>
            </label>
          )}
        </div>

        <div className="backup-destination">
          <h3>Ziel</h3>
          <div className="backup-destination-options">
            <label className="backup-radio">
              <input
                type="radio"
                name="destination"
                value="server"
                checked={destination === 'server'}
                onChange={(e) => setDestination(e.target.value)}
              />
              Auf Server speichern
            </label>
            <label className="backup-radio">
              <input
                type="radio"
                name="destination"
                value="download"
                checked={destination === 'download'}
                onChange={(e) => setDestination(e.target.value)}
              />
              Direkt herunterladen
            </label>
            <label className="backup-radio backup-radio-disabled">
              <input
                type="radio"
                name="destination"
                value="s3"
                disabled
              />
              S3 (nicht konfiguriert)
            </label>
          </div>
        </div>

        <button
          className="btn-primary backup-create-btn"
          onClick={handleCreateBackup}
          disabled={creating || selectedGroups.length === 0}
          aria-busy={creating}
        >
          {creating ? 'Erstelle Backup...' : 'Backup erstellen'}
        </button>

        {createMessage && (
          <div className={`backup-message backup-message-${createMessage.type}`} role="alert">
            {createMessage.text}
          </div>
        )}
      </section>

      {/* Section 2: Restore Backup */}
      <section className="backup-section" aria-labelledby="restore-backup-heading">
        <h2 id="restore-backup-heading">Backup wiederherstellen</h2>

        <div className="backup-restore-source">
          <h3>Quelle</h3>
          <div className="backup-destination-options">
            <label className="backup-radio">
              <input
                type="radio"
                name="restoreSource"
                value="server"
                checked={restoreSource === 'server'}
                onChange={(e) => {
                  setRestoreSource(e.target.value);
                  setInspectedManifest(null);
                  setRestoreResult(null);
                }}
              />
              Server-Backup
            </label>
            <label className="backup-radio">
              <input
                type="radio"
                name="restoreSource"
                value="upload"
                checked={restoreSource === 'upload'}
                onChange={(e) => {
                  setRestoreSource(e.target.value);
                  setInspectedManifest(null);
                  setRestoreResult(null);
                }}
              />
              Datei hochladen
            </label>
          </div>
        </div>

        {restoreSource === 'server' ? (
          <div className="backup-restore-server">
            <label htmlFor="restore-filename">Server-Backup auswählen:</label>
            <select
              id="restore-filename"
              value={restoreFilename}
              onChange={(e) => {
                setRestoreFilename(e.target.value);
                setInspectedManifest(null);
              }}
            >
              <option value="">-- Backup auswählen --</option>
              {serverBackups.map(b => (
                <option key={b.filename} value={b.filename}>
                  {b.filename} ({formatBytes(b.size)})
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="backup-restore-upload">
            <label htmlFor="restore-file">Backup-Datei auswählen (.tar.gz):</label>
            <input
              id="restore-file"
              type="file"
              accept=".tar.gz,.gz"
              onChange={(e) => {
                setUploadFile(e.target.files[0] || null);
                setInspectedManifest(null);
              }}
            />
          </div>
        )}

        <button
          className="btn-secondary"
          onClick={handleInspect}
          disabled={inspecting || (restoreSource === 'server' ? !restoreFilename : !uploadFile)}
        >
          {inspecting ? 'Prüfe...' : 'Backup prüfen'}
        </button>

        {inspectedManifest && (
          <div className="backup-manifest">
            <h3>Backup-Inhalt</h3>
            <div className="backup-manifest-info">
              <p><strong>Erstellt:</strong> {formatDate(inspectedManifest.created_at)}</p>
              <p><strong>Version:</strong> {inspectedManifest.version}</p>
              <p><strong>Medien:</strong> {inspectedManifest.include_media ? 'Ja' : 'Nein'}
                {inspectedManifest.media_files_count != null && ` (${inspectedManifest.media_files_count} Dateien)`}
              </p>
            </div>

            <h4>Tabellen zum Wiederherstellen:</h4>
            <div className="backup-restore-groups">
              {inspectedManifest.table_groups.map(gid => {
                const groupDef = tableGroups.find(g => g.id === gid);
                const rowCount = Object.entries(inspectedManifest.tables)
                  .filter(([name]) => groupDef?.tables.some(t => t.name === name))
                  .reduce((sum, [, count]) => sum + count, 0);
                return (
                  <label key={gid} className="backup-group-card">
                    <input
                      type="checkbox"
                      checked={restoreGroups.includes(gid)}
                      onChange={() => toggleRestoreGroup(gid)}
                    />
                    <div className="backup-group-info">
                      <span className="backup-group-label">{groupDef?.label || gid}</span>
                      <span className="backup-group-count">{rowCount.toLocaleString('de-DE')} Zeilen</span>
                    </div>
                  </label>
                );
              })}
            </div>

            {inspectedManifest.include_media && (
              <label className="backup-media-option">
                <input
                  type="checkbox"
                  checked={restoreMedia}
                  onChange={(e) => setRestoreMedia(e.target.checked)}
                />
                <span>Medien wiederherstellen ({inspectedManifest.media_files_count || 0} Dateien)</span>
              </label>
            )}

            <button
              className="btn-danger"
              onClick={() => setShowConfirmDialog(true)}
              disabled={restoring || restoreGroups.length === 0}
            >
              Wiederherstellen
            </button>
          </div>
        )}

        {showConfirmDialog && (
          <div className="backup-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="confirm-heading">
            <div className="backup-confirm-dialog">
              <h3 id="confirm-heading">Wiederherstellung bestätigen</h3>
              <p>
                Die ausgewählten Tabellen werden <strong>überschrieben</strong>.
                Dieser Vorgang kann nicht rückgängig gemacht werden.
              </p>
              <p>Ausgewählte Gruppen: {restoreGroups.join(', ')}</p>
              <div className="backup-confirm-actions">
                <button className="btn-secondary" onClick={() => setShowConfirmDialog(false)}>
                  Abbrechen
                </button>
                <button
                  className="btn-danger"
                  onClick={handleRestore}
                  disabled={restoring}
                  aria-busy={restoring}
                >
                  {restoring ? 'Stelle wieder her...' : 'Ja, wiederherstellen'}
                </button>
              </div>
            </div>
          </div>
        )}

        {restoreResult && (
          <div className={`backup-message backup-message-${restoreResult.type}`} role="alert">
            {restoreResult.type === 'success' ? (
              <div>
                <p>{restoreResult.data.message}</p>
                {restoreResult.data.restored_tables && (
                  <ul className="backup-restore-details">
                    {Object.entries(restoreResult.data.restored_tables).map(([table, count]) => (
                      <li key={table}>{table}: {count} Zeilen</li>
                    ))}
                  </ul>
                )}
                {restoreResult.data.media_files_restored != null && (
                  <p>Medien: {restoreResult.data.media_files_restored} Dateien wiederhergestellt</p>
                )}
              </div>
            ) : (
              restoreResult.text
            )}
          </div>
        )}
      </section>

      {/* Section 3: Server Backups */}
      <section className="backup-section" aria-labelledby="server-backups-heading">
        <div className="backup-section-header">
          <h2 id="server-backups-heading">Gespeicherte Backups</h2>
          <button className="btn-link" onClick={loadServerBackups} disabled={loadingBackups}>
            {loadingBackups ? 'Lade...' : 'Aktualisieren'}
          </button>
        </div>

        {serverBackups.length === 0 ? (
          <p className="backup-empty">Keine Backups auf dem Server vorhanden.</p>
        ) : (
          <table className="backup-table" aria-label="Gespeicherte Backups">
            <thead>
              <tr>
                <th>Dateiname</th>
                <th>Größe</th>
                <th>Erstellt</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {serverBackups.map(backup => (
                <tr key={backup.filename}>
                  <td>{backup.filename}</td>
                  <td>{formatBytes(backup.size)}</td>
                  <td>{formatDate(backup.created_at)}</td>
                  <td className="backup-actions">
                    <button
                      className="btn-small btn-primary"
                      onClick={() => handleDownloadBackup(backup.filename)}
                      aria-label={`${backup.filename} herunterladen`}
                    >
                      Download
                    </button>
                    <button
                      className="btn-small btn-danger"
                      onClick={() => handleDeleteBackup(backup.filename)}
                      aria-label={`${backup.filename} löschen`}
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

export default BackupRestore;
